import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { debounce } from 'lodash';

@Component({
    selector: 'medsurf-code-editor-link',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './link-editor-dialog.component.html',
    styleUrls: ['./link-editor-dialog.component.scss'],
    standalone: false
})
export class LinkEditorDialogComponent {
  
  #linkEditorDialog: FlatModels.EditorAuthorModels.LinkEditorDialog;
  linkForm = new FormGroup(
    {
      href: new FormControl(''),
      targetBlank: new FormControl(false),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.EditorAuthorModels.LinkEditorDialogSchema
      ),
      updateOn: 'change',
    }
  );

  linkType = signal<'text' | 'path' | 'uuid' | 'external'>('text');
  searchString = signal('');
  pendingSearch = signal(false);
  
  readonly FlatModels = FlatModels;

  constructor(public ref: DynamicDialogRef,
              public dynamicDialogConfig: DynamicDialogConfig,
              public searchControlFacade: FlatFacades.SearchControlFacade) {
    this.#linkEditorDialog = structuredClone(this.dynamicDialogConfig.data);
        
    // Patch data
    this.linkForm.reset({
      href: this.#linkEditorDialog.href,
      targetBlank: this.#linkEditorDialog.targetBlank || false,
    });
    this.searchString.set(this.#linkEditorDialog.href || '');

    // Setup
    this.search();
  }
  
  search() {
    let searchString = this.searchString();

    this.linkForm.setValue({
      href: '',
      targetBlank: this.linkForm.value.targetBlank || false
    })

    if (!searchString || searchString.length < 3) return;

    const medsurfMatch = searchString.match(/(medsurf\.(|preview\.|stage\.)iml|elearning\.medizin)\.unibe\.ch/);
    if (medsurfMatch) {
      searchString = searchString.replace(/^.*?unibe\.ch/, '')
    }
    const externalMatch = searchString.match(/^(http:|https:|)\/\//);
    const uuidMatch = searchString?.match(/\w{8}-\w{4}-\w{4}-\w{4}-\w{12}/);
    const isPath = searchString.startsWith('/');
    
    if (externalMatch) {
      this.linkType.set('external');
      const href = searchString.replace(/^\/\//, 'https://');
      this.linkForm.reset({
        href,
        targetBlank: this.linkForm.value.targetBlank || false
      })
      return;
    }

    if (uuidMatch) {
      this.linkType.set('uuid');
      const uuid = uuidMatch[0];
      this.fetchSearchResult({ uuid });
      return;
    }

    if (isPath) {
      this.linkType.set('path');
      const path = searchString.replace(/^\/|\/$/g, '');
      this.fetchSearchResult({ path });
      return;
    }

    this.linkType.set('text');
    this.fetchSearchResult({ searchString });
  }

  debouncedSearch = debounce(() => this.search(), 300);

  async fetchSearchResult(options: { uuid?: string, path?: string, searchString?: string}) {

    const rows = 10;
    const searchString = options.searchString || '';
    const ids = options.uuid ? [options.uuid] : undefined;
    const paths = options.path ? [options.path] : undefined;
    
    this.pendingSearch.set(true);
    this.searchControlFacade.requestFindNodes({
      first: 0,
      rows,
      searchString,
      ids,
      paths
    }).subscribe({
      next: async() => {
        this.pendingSearch.set(false)

        const result = this.searchControlFacade.snapshot_nodeSearchResult();
        if (result?.items?.length === 1) {
          this.onSelect(result.items[0]);
        }
      },
      error: () => this.pendingSearch.set(false)
    });
  }
  
  onSelect(data: any) {
    if (data.id) {
      this.linkForm.setValue({
        href: '/node/' + data.id,
        targetBlank: this.linkForm.value.targetBlank || false,
      })
    }
  }
  
  onModalSubmit() {
    if (this.linkForm.invalid) return;

    const returnData: FlatModels.EditorAuthorModels.LinkEditorDialog = {
      type: this.linkType() === 'external' ? 'external' : 'internal',
      href: this.linkForm.value.href || '',
      targetBlank: this.linkForm.value.targetBlank || false
    }
    
    this.ref.close(returnData);
  }
}
